import * as yup from 'yup';

import { IAttributeDef } from '@/src/config/AttributesConfig';
import { ILooseObject } from '@/src/types/common.interface';

class SchemaValidations {
  public static buildMetaDataRowSchema(fields: IAttributeDef[]): yup.ObjectSchema<ILooseObject> {
    const shape: ILooseObject = {};
    fields.forEach((field) => {
      let schema: yup.AnySchema;
      if (field.value_type === 'text') {
        schema = yup.string().trim();
      } else if (field.value_type === 'number') {
        schema = yup.number().typeError(`${field.ui_label} must be a valid number`);
      } else if (field.value_type === 'boolean') {
        schema = yup.boolean();
      } else if (field.value_type === 'array') {
        schema = yup.array();
      } else {
        schema = yup.mixed();
      }
      if (field.is_mandatory) {
        schema = schema.required(`${field.ui_label} is required`);
      } else {
        schema = schema.nullable();
      }
      shape[field.name] = schema;
    });
    return yup.object().shape(shape);
  }

  public static AddUsers(fields: IAttributeDef[]): yup.ObjectSchema<ILooseObject> {
    const shape: ILooseObject = {};

    fields.forEach((field) => {
      let schema: yup.AnySchema;

      // Step 1: Determine the base schema based on value_type
      if (field.value_type === 'text') {
        schema = yup.string().trim();
      } else if (field.value_type === 'number') {
        schema = yup.number().typeError(`${field.ui_label} must be a valid number`);
      } else if (field.value_type === 'boolean') {
        schema = yup.boolean();
      } else {
        schema = yup.mixed();
      }

      // Step 2: Apply mandatory validation if is_mandatory is true
      if (field.is_mandatory) {
        schema = schema.required(`${field.ui_label} is required`);
      } else {
        schema = schema.nullable();
      }

      // Step 2: Apply specific validations for certain fields
      if (field.name === 'name') {
        schema = (schema as yup.StringSchema)
          .min(5, `${field.ui_label} must be at least 5 characters`)
          .max(50, `${field.ui_label} must not exceed 50 characters`);
      } else if (field.name === 'email_id') {
        schema = (schema as yup.StringSchema).matches(
          /^[a-zA-Z0-9._%+-]{2,}@[a-zA-Z0-9.-]{2,}\.[a-zA-Z]{2,}$/,
          `${field.ui_label} must be a valid email address`
        );
      } else if (field.name === 'mobile') {
        schema = (schema as yup.StringSchema).test(
          'is-valid-mobile',
          `${field.ui_label} must be a 10-digit number`,
          (value) => /^\d{10}$/.test(value?.toString() || '')
        );
      }

      // Add the schema to the shape object
      shape[field.name] = schema;
    });
    // Add metadata array validation
    shape.metadata = yup.array().of(
      yup.object().shape({
        selectedAttributeName: yup.string().required('Attribute is required'),
        attributeValue: yup.mixed().test('value-required', 'Value is required', function (value) {
          const { selectedAttributeName } = this.parent;
          if (!selectedAttributeName) return true; // No attribute selected, no validation needed

          // Check if value is empty based on its type
          let isEmpty = false;
          if (Array.isArray(value)) {
            isEmpty = value.length === 0;
          } else if (typeof value === 'string') {
            isEmpty = value.trim() === '';
          } else {
            isEmpty = !value;
          }

          if (isEmpty) {
            return this.createError({ message: 'Value is required' });
          }

          return true;
        })
      })
    );
    return yup.object().shape(shape);
  }

  public static AddApiConfig(fields: IAttributeDef[]): yup.ObjectSchema<ILooseObject> {
    const shape: ILooseObject = {};

    fields.forEach((field) => {
      let schema: yup.AnySchema = yup.mixed();

      // Step 1: Determine the base schema based on value_type
      if (field.value_type === 'text') {
        schema = yup.string().trim();
      } else if (field.value_type === 'boolean') {
        schema = yup.boolean();
      } else {
        schema = yup.mixed();
      }

      // Step 2: Apply mandatory validation if is_mandatory is true
      if (field.is_mandatory) {
        schema = schema.required(`${field.ui_label} is required`);
      } else {
        schema = schema.nullable();
      }

      // Step 3: Apply special validation for 'uri' field
      if (field.name === 'uri') {
        schema = yup
          .string()
          .trim()
          .matches(
            /^\/[a-zA-Z0-9_{}-]+(?:\/[a-zA-Z0-9_{}-]+)*$/,
            `${field.ui_label} must be a valid URL path`
          );
      } else if (field.name === 'version') {
        schema = yup.number().min(0, `${field.ui_label} must be a non-negative number`);
      }

      shape[field.name] = schema;
    });

    return yup.object().shape(shape);
  }

  public static AddAppLevelCredentials(fields: IAttributeDef[]): yup.ObjectSchema<ILooseObject> {
    const shape: ILooseObject = {};

    fields.forEach((field) => {
      let schema: any;
      // Step 1: Determine the base schema based on value_type
      if (field.value_type === 'text') {
        schema = yup.string().trim();
      } else if (field.value_type === 'boolean') {
        schema = yup.boolean();
      } else if (field.value_type === 'array') {
        schema = yup.array();
      } else {
        schema = yup.mixed();
      }

      // Step 2: Apply mandatory validation if is_mandatory is true
      if (field.is_mandatory) {
        schema = schema.required(`${field.ui_label} is required`);
      }
      if (field.value_type === 'array') {
        schema = schema.min(1, `${field.ui_label} is required`);
      } else {
        schema = schema.nullable();
      }

      shape[field.name] = schema;
    });

    return yup.object().shape(shape);
  }

  public static AddPolicy(fields: IAttributeDef[]): yup.ObjectSchema<ILooseObject> {
    const shape: ILooseObject = {};

    fields.forEach((field) => {
      let schema: yup.AnySchema = yup.mixed();

      // Step 1: Determine the base schema based on value_type
      if (field.value_type === 'text') {
        schema = yup.string().trim();
      } else if (field.value_type === 'boolean') {
        schema = yup.boolean();
      } else {
        schema = yup.mixed();
      }

      // Step 2: Apply mandatory validation if is_mandatory is true
      if (field.is_mandatory) {
        schema = schema.required(`${field.ui_label} is required`);
      } else {
        schema = schema.nullable();
      }

      // Step 3: Apply special validation for policy fields
      if (field.name === 'policy_name') {
        schema = (schema as yup.StringSchema)
          .min(3, `${field.ui_label} must be at least 3 characters`)
          .max(100, `${field.ui_label} must not exceed 100 characters`);
      } else if (field.name === 'policy_description') {
        schema = (schema as yup.StringSchema)
          .min(10, `${field.ui_label} must be at least 10 characters`)
          .max(100, `${field.ui_label} must not exceed 100 characters`);
      } else if (field.name === 'policy_value') {
        schema = (schema as yup.StringSchema)
          .min(10, `${field.ui_label} must be at least 10 characters`)
          .max(1000, `${field.ui_label} must not exceed 1000 characters`);
      }

      shape[field.name] = schema;
    });

    return yup.object().shape(shape);
  }

  public static AddRoles(fields: IAttributeDef[]): yup.ObjectSchema<ILooseObject> {
    const shape: ILooseObject = {};

    fields.forEach((field) => {
      let schema: yup.AnySchema = yup.mixed();

      // Step 1: Determine the base schema based on value_type
      if (field.value_type === 'text') {
        schema = yup.string().trim();
      } else if (field.value_type === 'number') {
        schema = yup.number().typeError(`${field.ui_label} must be a valid number`);
      } else if (field.value_type === 'boolean') {
        schema = yup.boolean();
      } else if (field.value_type === 'array') {
        schema = yup
          .array()
          .transform((value, originalValue) =>
            typeof originalValue === 'string' && originalValue.trim() === '' ? undefined : value
          );
      } else {
        schema = yup.mixed();
      }

      // Step 2: Apply mandatory validation if is_mandatory is true
      if (field.is_mandatory) {
        if (field.value_type === 'array') {
          schema = schema
            .transform((value, originalValue) =>
              Array.isArray(originalValue) ? originalValue : undefined
            )
            .required(`${field.ui_label} is required`)
            .test(
              'min-length',
              `At least one ${field.ui_label} is required`,
              (value: string) => value && value.length > 0
            );
        } else {
          schema = schema.required(`${field.ui_label} is required`);
        }
      } else {
        schema = schema.nullable();
      }

      // Add the schema to the shape object
      shape[field.name] = schema;
    });

    return yup.object().shape(shape);
  }

  public static CreatePermission(fields: IAttributeDef[]): yup.ObjectSchema<ILooseObject> {
    const shape: ILooseObject = {};

    fields.forEach((field) => {
      let schema: yup.AnySchema = yup.mixed();

      // Step 1: Determine the base schema based on value_type
      if (field.value_type === 'text') {
        schema = yup.string().trim();
      } else if (field.value_type === 'number') {
        schema = yup.number().typeError(`${field.ui_label} must be a valid number`);
      } else if (field.value_type === 'boolean') {
        schema = yup.boolean();
      } else {
        schema = yup.mixed();
      }

      // Step 2: Apply mandatory validation if is_mandatory is true
      if (field.is_mandatory) {
        schema = schema.required(`${field.ui_label} is required`);
      } else {
        schema = schema.nullable();
      }

      // Add the schema to the shape object
      shape[field.name] = schema;
    });

    return yup.object().shape(shape);
  }

  public static AddPermission(fields: IAttributeDef[]): yup.ObjectSchema<ILooseObject> {
    const shape: ILooseObject = {};

    fields.forEach((field) => {
      let schema: yup.AnySchema = yup.mixed();

      // Step 1: Determine the base schema based on value_type
      if (field.value_type === 'text') {
        schema = yup.string().trim();
      } else if (field.value_type === 'number') {
        schema = yup.number().typeError(`${field.ui_label} must be a valid number`);
      } else if (field.value_type === 'boolean') {
        schema = yup.boolean();
      } else {
        schema = yup.mixed();
      }

      // Step 2: Apply mandatory validation if is_mandatory is true
      if (field.is_mandatory) {
        schema = schema.required(`${field.ui_label} is required`);
      } else {
        schema = schema.nullable();
      }

      // Add the schema to the shape object
      shape[field.name] = schema;
    });

    return yup.object().shape(shape);
  }

  public static AddPolicyFieldsSchema(fields: IAttributeDef[]): yup.ObjectSchema<ILooseObject> {
    const shape: ILooseObject = {};

    fields.forEach((field) => {
      let schema: yup.AnySchema = yup.mixed();

      if (field.value_type === 'text') {
        schema = yup.string().trim();
      } else if (field.value_type === 'boolean') {
        schema = yup.boolean();
      } else {
        schema = yup.mixed();
      }

      if (field.is_mandatory) {
        schema = schema.required(`${field.ui_label} is required`);
      } else {
        schema = schema.nullable();
      }

      if (field.name === 'uri') {
        schema = yup
          .string()
          .trim()
          .matches(
            /^\/[a-zA-Z0-9_{}-]+(?:\/[a-zA-Z0-9_{}-]+)*$/,
            `${field.ui_label} must be a valid URL path`
          );
      }

      shape[field.name] = schema;
    });

    return yup.object().shape(shape);
  }

  public static AddTestCasesSchema(): yup.ObjectSchema<ILooseObject> {
    const shape: ILooseObject = {};

    // Add testCases validation
    shape.testCases = yup.array().of(
      yup.object().shape({
        test_case_name: yup.string().required('Test case name is required'),
        api_config: yup.string().required('API config is required'),
        organization: yup.string().required('Organization is required'),
        user_name: yup.string().required('User name is required'),
        user_type: yup.string().required('User type is required'),
        role_names: yup.array().min(1, 'At least one role is required'),
        params: yup.array().of(
          yup.object().shape(
            {
              key: yup.string().when('value', {
                is: (val: any) => val !== undefined && val !== '',
                then: (schema) => schema.required('Key is required if value is provided'),
                otherwise: (schema) => schema.nullable()
              }),
              value: yup.string().when('key', {
                is: (val: any) => val !== undefined && val !== '',
                then: (schema) => schema.required('Value is required if key is provided'),
                otherwise: (schema) => schema.nullable()
              })
            },
            [['key', 'value']]
          )
        ),
        headers: yup.array().of(
          yup.object().shape(
            {
              key: yup.string().when('value', {
                is: (val: any) => val !== undefined && val !== '',
                then: (schema) => schema.required('Key is required if value is provided'),
                otherwise: (schema) => schema.nullable()
              }),
              value: yup.string().when('key', {
                is: (val: any) => val !== undefined && val !== '',
                then: (schema) => schema.required('Value is required if key is provided'),
                otherwise: (schema) => schema.nullable()
              })
            },
            [['key', 'value']]
          )
        ),
        user_attributes: yup.array().of(
          yup.object().shape({
            key: yup.string().required('Attribute is required'),
            value: yup.mixed().required('Value is required')
          })
        ),
        body: yup
          .string()
          .nullable()
          .test('is-json', 'Invalid JSON format', (value) => {
            if (!value) return true;
            try {
              JSON.parse(value);
              return true;
            } catch (e) {
              return false;
            }
          })
      })
    );

    return yup.object().shape(shape);
  }
}

export default SchemaValidations;
